import { createContext, type ReactNode, useContext } from "react";

/**
 * Router-agnostic routing surface for the templates editor and templates list.
 *
 * The editor core and list never import a router directly; instead each host
 * (track-office on TanStack Router, baback-office on react-router-dom) provides
 * the current shopId and navigation callbacks through this context. This is what
 * lets the same core power both the tracking editor (TemplatesEditor) and the
 * return editor (ReturnTemplatesEditor).
 */
export type TemplatesEditorRouting = {
  shopId: string;
  /** Navigate to a template's editor page. */
  openTemplate?: (templateId: string) => void;
  /** Navigate to a workflow's editor page (linked-notifications shortcut). */
  openWorkflow?: (workflowId: string) => void;
};

const TemplatesEditorRoutingContext = createContext<
  TemplatesEditorRouting | undefined
>(undefined);

export function TemplatesEditorRoutingProvider({
  value,
  children,
}: {
  value: TemplatesEditorRouting;
  children: ReactNode;
}) {
  return (
    <TemplatesEditorRoutingContext.Provider value={value}>
      {children}
    </TemplatesEditorRoutingContext.Provider>
  );
}

export function useTemplatesEditorRouting(): TemplatesEditorRouting {
  const context = useContext(TemplatesEditorRoutingContext);

  if (context === undefined) {
    throw new Error(
      "useTemplatesEditorRouting must be used within a TemplatesEditorRoutingProvider",
    );
  }
  return context;
}
